/**
 * Lekkasjesjekk mot Have I Been Pwned (Pwned Passwords) med k-anonymitet.
 *
 * Personvern (nullkunnskap bevares):
 *  - Passordet hashes lokalt (SHA-1, kun fordi det er HIBP-protokollen – ikke lagring).
 *  - KUN de 5 første hex-tegnene av hashen sendes til api.pwnedpasswords.com.
 *  - Tjenesten svarer med alle suffikser i det området (~800–1000 stk); sammenligningen skjer lokalt.
 *  - Verken passord, full hash eller resultat sendes til Controlle-serveren eller logges.
 *  - «Add-Padding: true» gjør at svarstørrelsen ikke avslører hvilket område som ble spurt om.
 *
 * Ingen API-nøkkel kreves for dette endepunktet.
 */

export const HIBP_RANGE_URL = "https://api.pwnedpasswords.com/range";
const MAX_CACHED_RANGES = 400;

/** Områdecache (prefix -> Map(suffix -> antall)). Kun i minnet for økten. */
const rangeCache = new Map();

const toHex = (buf) => Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("").toUpperCase();

export async function sha1Hex(value) {
  const digest = await window.crypto.subtle.digest("SHA-1", new TextEncoder().encode(value));
  return toHex(digest);
}

/** Parser «SUFFIX:COUNT»-linjer. Padding-rader (count 0) og ugyldige rader ignoreres. */
export function parseRange(body) {
  const records = new Map();
  for (const line of String(body || "").split(/\r?\n/)) {
    const sep = line.indexOf(":");
    if (sep <= 0) continue;
    const suffix = line.slice(0, sep).trim().toUpperCase();
    const count = Number.parseInt(line.slice(sep + 1).trim(), 10);
    if (/^[0-9A-F]{35}$/.test(suffix) && Number.isSafeInteger(count) && count > 0) records.set(suffix, count);
  }
  return records;
}

export class BreachServiceError extends Error {
  constructor(message, { status, retryable = false } = {}) {
    super(message);
    this.name = "BreachServiceError";
    this.status = status;
    this.retryable = retryable;
  }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function fetchRange(prefix, { signal, fetchImpl = fetch } = {}) {
  const cached = rangeCache.get(prefix);
  if (cached) return cached;
  let lastErr = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    if (signal?.aborted) throw new DOMException("Avbrutt", "AbortError");
    try {
      const res = await fetchImpl(`${HIBP_RANGE_URL}/${prefix}`, { method: "GET", headers: { "Add-Padding": "true" }, credentials: "omit", cache: "no-store", signal });
      if (res.status === 429 || res.status >= 500) {
        lastErr = new BreachServiceError(`Lekkasjetjenesten svarte ${res.status}`, { status: res.status, retryable: true });
        await sleep(600 * (attempt + 1));
        continue;
      }
      if (!res.ok) throw new BreachServiceError(`Lekkasjetjenesten avviste forespørselen (${res.status})`, { status: res.status });
      const records = parseRange(await res.text());
      if (rangeCache.size >= MAX_CACHED_RANGES) rangeCache.delete(rangeCache.keys().next().value);
      rangeCache.set(prefix, records);
      return records;
    } catch (e) {
      if (e?.name === "AbortError" || e instanceof BreachServiceError && !e.retryable) throw e;
      lastErr = e instanceof BreachServiceError ? e : new BreachServiceError("Fikk ikke kontakt med lekkasjetjenesten. Sjekk nettforbindelsen.", { retryable: true });
      await sleep(600 * (attempt + 1));
    }
  }
  throw lastErr || new BreachServiceError("Lekkasjesjekk mislyktes");
}

/**
 * Sjekker mange passord. Dedupliserer passord og prefiks, begrenset parallellitet.
 * @param {string[]} passwords
 * @returns {Promise<Map<string, number>>} passord -> antall ganger sett i lekkasjer (0 = ikke funnet)
 */
export async function checkPasswords(passwords, { concurrency = 3, signal, onProgress, fetchImpl } = {}) {
  const unique = Array.from(new Set((passwords || []).filter((p) => typeof p === "string" && p.length > 0)));
  const hashes = new Map(); // passord -> hash
  for (const p of unique) hashes.set(p, await sha1Hex(p));
  const byPrefix = new Map(); // prefix -> [passord]
  for (const [p, h] of hashes) {
    const pre = h.slice(0, 5);
    if (!byPrefix.has(pre)) byPrefix.set(pre, []);
    byPrefix.get(pre).push(p);
  }
  const prefixes = Array.from(byPrefix.keys());
  const results = new Map(unique.map((p) => [p, 0]));
  let next = 0;
  let done = 0;
  const worker = async () => {
    while (next < prefixes.length) {
      const pre = prefixes[next++];
      const records = await fetchRange(pre, { signal, fetchImpl });
      for (const p of byPrefix.get(pre)) results.set(p, records.get(hashes.get(p).slice(5)) || 0);
      done++;
      onProgress?.(done, prefixes.length);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, prefixes.length) }, worker));
  return results;
}

/** Sjekker ett passord. */
export async function checkPassword(password, opts) {
  if (!password) return { pwned: false, count: 0 };
  const r = await checkPasswords([password], opts);
  const count = r.get(password) || 0;
  return { pwned: count > 0, count };
}

/** Kun for tester. */
export function _clearRangeCache() {
  rangeCache.clear();
}

export const formatBreachCount = (n) => (n >= 1000000 ? `${(n / 1000000).toFixed(n >= 10000000 ? 0 : 1).replace(".", ",")} mill.` : new Intl.NumberFormat("nb-NO").format(n));
